import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { StatusTone } from '@/components/status-badge';
import type { ProfitView } from '@/features/catalog/api/products';
import { apiRequest } from '@/lib/http';

/** Em que pé a venda está para a operação (regra no servidor, `orders.service.ts`). */
export type OrderStage =
  | 'PAYMENT_PENDING'
  | 'TO_SHIP'
  | 'IN_TRANSIT'
  | 'PROBLEM'
  | 'DELIVERED'
  | 'NO_TRACKING'
  | 'CHECKING'
  | 'CANCELLED';

export const STAGE_META: Record<OrderStage, { label: string; tone: StatusTone }> = {
  PAYMENT_PENDING: { label: 'Aguardando pagamento', tone: 'neutral' },
  TO_SHIP: { label: 'A enviar', tone: 'warning' },
  IN_TRANSIT: { label: 'Em trânsito', tone: 'info' },
  PROBLEM: { label: 'Com problema', tone: 'danger' },
  DELIVERED: { label: 'Entregue', tone: 'success' },
  NO_TRACKING: { label: 'Sem rastreio', tone: 'neutral' },
  // Carga histórica: o envio existe, mas o troig ainda não consultou a situação dele.
  CHECKING: { label: 'Conferindo envio', tone: 'neutral' },
  CANCELLED: { label: 'Cancelado', tone: 'neutral' },
};

/** Ordem dos filtros: abertos (até a entrega) primeiro, como a operação lê. */
export const OPEN_STAGES: OrderStage[] = ['PAYMENT_PENDING', 'TO_SHIP', 'IN_TRANSIT', 'PROBLEM'];
export const ALL_STAGES: OrderStage[] = [
  ...OPEN_STAGES,
  'DELIVERED',
  'NO_TRACKING',
  'CHECKING',
  'CANCELLED',
];

export type RealizedMissing = 'SALE_FEE' | 'SHIPPING' | 'PRODUCT_COST' | 'TAX' | 'PACKAGING';

export const MISSING_LABELS: Record<RealizedMissing, string> = {
  SALE_FEE: 'Tarifa',
  SHIPPING: 'Frete',
  PRODUCT_COST: 'Custo da mercadoria',
  TAX: 'Imposto',
  PACKAGING: 'Embalagem',
};

/** Resultado realizado da venda (ADR-048); nulos = desconhecidos, nunca zero. */
export interface RealizedSale {
  status: 'COMPLETE' | 'INCOMPLETE' | 'CANCELLED';
  revenue: string;
  saleFees: string | null;
  shipping: string | null;
  productCost: string | null;
  tax: string | null;
  packaging: string | null;
  additional: string;
  /** Custo de devolução cobrado do vendedor; zero sem devolução. */
  returns: string;
  /** Com componente faltando é um teto: o real é esse ou menor. */
  result: string | null;
  margin: string | null;
  /** Algum valor não é o do dia da venda (custo atual, embalagem ou adicionais de hoje). */
  approximate: boolean;
  missing: RealizedMissing[];
  view: ProfitView | null;
}

export interface OrderSummary {
  id: string;
  externalId: string;
  packId: string | null;
  store: { id: string; name: string; provider: string };
  orderedAt: string;
  stage: OrderStage;
  rawStatus: string;
  totalAmount: string;
  items: {
    title: string;
    sku: string | null;
    quantity: number;
    productId: string | null;
    /** Capa do anúncio vendido; nula = anúncio sem foto. */
    coverUrl: string | null;
  }[];
  shipment: {
    status: string;
    deliveredAt: string | null;
    /** Horário de corte da plataforma para despachar; só enquanto está para enviar. */
    shipBy: string | null;
  } | null;
  /** Ausente para quem não vê financeiro. */
  realized?: RealizedSale;
  /** Quem recebe, resumido; nulo = não informado ou já apagado (90 dias após a entrega). */
  recipient: { name: string | null; city: string | null; state: string | null } | null;
}

/** Nome e endereço de quem recebe (ADR-053). */
export interface ShipmentRecipient {
  name: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface OrderDetail extends OrderSummary {
  closedAt: string | null;
  cancelReason: string | null;
  lines: {
    id: string;
    title: string;
    sku: string | null;
    quantity: number;
    unitPrice: string;
    listingId: string | null;
    productId: string | null;
    externalItemId: string;
    coverUrl: string | null;
    listingType: string | null;
    saleFeePerUnit?: string | null;
    unitCost?: string | null;
    unitCostSource?: 'SALE_DATE' | 'CURRENT_APPROX' | null;
  }[];
  shipmentCost?: { total: string | null; sharedWith: number } | null;
  recipientAddress: ShipmentRecipient | null;
  /** Nome e endereço já apagados pelo prazo de guarda. */
  recipientErased: boolean;
}

export interface OrderPage {
  items: OrderSummary[];
  total: number;
  page: number;
  pageSize: number;
  stageCounts: Record<OrderStage, number>;
}

export interface OrderPlatform {
  provider: string;
  displayName: string;
  stores: { id: string; name: string }[];
}

/** Prazo de despacho dos "a enviar", no dia da empresa. */
export type ShipDeadline = 'LATE' | 'TODAY' | 'TOMORROW' | 'LATER' | 'NONE';

export interface OrdersDashboard {
  deadlines: Record<ShipDeadline, { count: number; nextDeadline: string | null }>;
  inTransit: number;
  problem: number;
  paymentPending: number;
}

export interface OrderFilter {
  view: 'open' | 'all';
  provider: string;
  connectionId?: string;
  stage?: OrderStage;
  /** Cartão do painel: os "a enviar" com este prazo. */
  deadline?: ShipDeadline;
  search?: string;
  /** `AAAA-MM-DD`, pela data da venda. */
  from?: string;
  to?: string;
  page: number;
}

export const orderKeys = {
  all: ['orders'] as const,
  platforms: () => [...orderKeys.all, 'platforms'] as const,
  dashboard: (provider: string, connectionId?: string) =>
    [...orderKeys.all, 'dashboard', provider, connectionId ?? ''] as const,
  list: (filter: OrderFilter) => [...orderKeys.all, 'list', filter] as const,
  detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
};

export function useOrderPlatforms() {
  return useQuery({
    queryKey: orderKeys.platforms(),
    queryFn: () => apiRequest<OrderPlatform[]>('/orders/platforms'),
  });
}

/**
 * Lista da aba. Recarrega a cada minuto enquanto a tela está aberta: a venda chega pelo aviso da
 * plataforma em segundos (ADR-047), e a lista de abertos é para acompanhar a operação.
 */
export function useOrders(filter: OrderFilter) {
  return useQuery({
    queryKey: orderKeys.list(filter),
    queryFn: () =>
      apiRequest<OrderPage>(
        `/orders?${new URLSearchParams({
          view: filter.view,
          provider: filter.provider,
          page: String(filter.page),
          pageSize: '25',
          ...(filter.connectionId ? { connectionId: filter.connectionId } : {}),
          ...(filter.stage ? { stage: filter.stage } : {}),
          ...(filter.deadline ? { deadline: filter.deadline } : {}),
          ...(filter.search ? { search: filter.search } : {}),
          ...(filter.from ? { from: filter.from } : {}),
          ...(filter.to ? { to: filter.to } : {}),
        }).toString()}`,
      ),
    enabled: Boolean(filter.provider),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** Cartões do painel; recarrega com a lista, a cada minuto — o horário de corte anda. */
export function useOrdersDashboard(provider: string, connectionId?: string) {
  return useQuery({
    queryKey: orderKeys.dashboard(provider, connectionId),
    queryFn: () =>
      apiRequest<OrdersDashboard>(
        `/orders/dashboard?${new URLSearchParams({
          provider,
          ...(connectionId ? { connectionId } : {}),
        }).toString()}`,
      ),
    enabled: Boolean(provider),
    refetchInterval: 60_000,
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => apiRequest<OrderDetail>(`/orders/${id}`),
  });
}
